/**
 * A headline number. Not a chart: five single values with no comparison to draw
 * are a KPI row, and a bar chart of them would invent a relationship between
 * "centers" and "warnings" that does not exist.
 *
 * Tone tints the value and the card's top edge. It is reinforcement only — the
 * number and its label already carry the meaning — so a tile with no tone is
 * the normal case, and a coloured one should be rare enough to mean something.
 * That is the whole design brief in miniature: healthy is quiet.
 */

import type { ReactNode } from 'react';

import type { Tone } from './Badge';

export type MetricTone = Extract<Tone, 'good' | 'warning' | 'serious' | 'critical' | 'neutral'>;

export function StatTile({
  label,
  value,
  hint,
  tone,
  icon,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: MetricTone;
  icon?: ReactNode;
}) {
  return (
    <div className={`metric${tone ? ` metric--${tone}` : ''}`}>
      <div className="metric__head">
        <span className="metric__label">{label}</span>
        {icon ? <span className="metric__icon">{icon}</span> : null}
      </div>
      <span className="metric__value">{value}</span>
      {hint ? <span className="metric__hint">{hint}</span> : null}
    </div>
  );
}

/** The brief's name for it. `StatTile` stays so existing pages don't move. */
export const MetricCard = StatTile;

export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="metric-grid">{children}</div>;
}

export const MetricGrid = StatGrid;
